import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/admin';
import { 
  Users, BarChart3, Network, Settings, DollarSign, TrendingUp, 
  Search, Filter, Eye, Ban, CheckCircle, XCircle, AlertCircle,
  ChevronRight, User, Calendar, Award, Loader, X, UserPlus, Edit, Trash2, Plus
} from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';

const AdminAffiliations = () => {
  const { locale } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Overview stats
  const [stats, setStats] = useState(null);
  
  // Network Explorer
  const [networkSearch, setNetworkSearch] = useState('');
  const [selectedUserNetwork, setSelectedUserNetwork] = useState(null);
  const [networkLoading, setNetworkLoading] = useState(false);
  
  // Referrals Management
  const [referrals, setReferrals] = useState([]);
  const [referralFilters, setReferralFilters] = useState({
    referrerId: '',
    referredId: '',
    level: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  });
  
  // Earnings
  const [earnings, setEarnings] = useState([]);
  const [earningsFilters, setEarningsFilters] = useState({
    referrerId: '',
    referredId: '',
    level: '',
    earningsType: '',
    dateFrom: '',
    dateTo: ''
  });
  
  // Top Referrers
  const [topReferrers, setTopReferrers] = useState([]);
  
  // Change Referrer Modal
  const [changeReferrerModal, setChangeReferrerModal] = useState({ open: false, referralId: null, newReferrerId: '' });
  
  // Earnings Correction Modals
  const [editEarningsModal, setEditEarningsModal] = useState({ open: false, earning: null, newAmount: '' });
  const [createEarningsModal, setCreateEarningsModal] = useState({ 
    open: false, 
    referrerId: '', 
    referredId: '', 
    level: '1', 
    earningsType: 'buyin', 
    amount: '', 
    percentage: '' 
  });

  useEffect(() => {
    if (activeTab === 'overview') {
      loadStats();
      loadTopReferrers();
    } else if (activeTab === 'referrals') {
      loadReferrals();
    } else if (activeTab === 'earnings') {
      loadEarnings();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'referrals') {
      loadReferrals();
    }
  }, [referralFilters]);

  useEffect(() => {
    if (activeTab === 'earnings') {
      loadEarnings();
    }
  }, [earningsFilters]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getAffiliationStats();
      if (response?.success) {
        setStats(response.stats);
      }
    } catch (err) {
      console.error('Error loading stats:', err);
      showMessage('error', err.message || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const loadTopReferrers = async () => {
    try {
      const response = await adminApi.getTopReferrers(10);
      if (response?.success) {
        setTopReferrers(response.topReferrers || []);
      }
    } catch (err) {
      console.error('Error loading top referrers:', err);
    }
  };

  const loadReferrals = async () => {
    setLoading(true);
    try {
      const filters = { ...referralFilters };
      if (filters.referrerId) filters.referrerId = parseInt(filters.referrerId);
      if (filters.referredId) filters.referredId = parseInt(filters.referredId);
      if (filters.level) filters.level = parseInt(filters.level);
      
      const response = await adminApi.getReferrals(filters);
      if (response?.success) {
        setReferrals(response.referrals || []);
      }
    } catch (err) {
      console.error('Error loading referrals:', err);
      showMessage('error', err.message || 'Failed to load referrals');
    } finally {
      setLoading(false);
    }
  };

  const loadEarnings = async () => {
    setLoading(true);
    try {
      const filters = { ...earningsFilters };
      if (filters.referrerId) filters.referrerId = parseInt(filters.referrerId);
      if (filters.referredId) filters.referredId = parseInt(filters.referredId);
      if (filters.level) filters.level = parseInt(filters.level);
      
      const response = await adminApi.getReferralEarnings(filters);
      if (response?.success) {
        setEarnings(response.earnings || []);
      }
    } catch (err) {
      console.error('Error loading earnings:', err);
      showMessage('error', err.message || 'Failed to load earnings');
    } finally {
      setLoading(false);
    }
  };

  const loadUserNetwork = async (userId) => {
    setNetworkLoading(true);
    try {
      const response = await adminApi.getUserReferralNetwork(userId);
      if (response?.success) {
        setSelectedUserNetwork(response.network);
      }
    } catch (err) {
      console.error('Error loading user network:', err);
      showMessage('error', err.message || 'Failed to load user network');
    } finally {
      setNetworkLoading(false);
    }
  };

  const handleUpdateReferralStatus = async (referralId, newStatus) => {
    setLoading(true);
    try {
      const response = await adminApi.updateReferralStatus(referralId, newStatus);
      if (response?.success) {
        showMessage('success', `Referral status updated to ${newStatus}`);
        loadReferrals();
      }
    } catch (err) {
      console.error('Error updating referral status:', err);
      showMessage('error', err.message || 'Failed to update referral status');
    } finally {
      setLoading(false);
    }
  };

  const handleBlockUserAffiliation = async (userId) => {
    if (!window.confirm('Are you sure you want to block all affiliations for this user?')) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await adminApi.blockUserAffiliation(userId);
      if (response?.success) {
        showMessage('success', 'User affiliation blocked successfully');
        if (activeTab === 'network' && selectedUserNetwork?.user?.id === userId) {
          loadUserNetwork(userId);
        }
      }
    } catch (err) {
      console.error('Error blocking user affiliation:', err);
      showMessage('error', err.message || 'Failed to block user affiliation');
    } finally {
      setLoading(false);
    }
  };

  const handleUnblockUserAffiliation = async (userId) => {
    setLoading(true);
    try {
      const response = await adminApi.unblockUserAffiliation(userId);
      if (response?.success) {
        showMessage('success', 'User affiliation unblocked successfully');
        if (activeTab === 'network' && selectedUserNetwork?.user?.id === userId) {
          loadUserNetwork(userId);
        }
      }
    } catch (err) {
      console.error('Error unblocking user affiliation:', err);
      showMessage('error', err.message || 'Failed to unblock user affiliation');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeReferrer = async () => {
    if (!changeReferrerModal.newReferrerId || isNaN(parseInt(changeReferrerModal.newReferrerId))) {
      showMessage('error', 'Please enter a valid referrer ID');
      return;
    }

    setLoading(true);
    try {
      const response = await adminApi.changeReferrer(
        changeReferrerModal.referralId,
        parseInt(changeReferrerModal.newReferrerId)
      );
      if (response?.success) {
        showMessage('success', 'Referrer changed successfully');
        setChangeReferrerModal({ open: false, referralId: null, newReferrerId: '' });
        loadReferrals();
      }
    } catch (err) {
      console.error('Error changing referrer:', err);
      showMessage('error', err.message || 'Failed to change referrer');
    } finally {
      setLoading(false);
    }
  };

  const handleEditEarnings = async () => {
    if (!editEarningsModal.earning || !editEarningsModal.newAmount || isNaN(parseFloat(editEarningsModal.newAmount))) {
      showMessage('error', 'Please enter a valid amount');
      return;
    }

    setLoading(true);
    try {
      const response = await adminApi.updateReferralEarnings(
        editEarningsModal.earning.id,
        parseFloat(editEarningsModal.newAmount)
      );
      if (response?.success) {
        showMessage('success', 'Earnings updated successfully');
        setEditEarningsModal({ open: false, earning: null, newAmount: '' });
        loadEarnings();
      }
    } catch (err) {
      console.error('Error updating earnings:', err);
      showMessage('error', err.message || 'Failed to update earnings');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEarnings = async (earningsId) => {
    if (!window.confirm('Are you sure you want to delete this earnings record? This will also deduct the amount from the user\'s balance.')) {
      return;
    }

    setLoading(true);
    try {
      const response = await adminApi.deleteReferralEarnings(earningsId);
      if (response?.success) {
        showMessage('success', 'Earnings deleted successfully');
        loadEarnings();
      }
    } catch (err) {
      console.error('Error deleting earnings:', err);
      showMessage('error', err.message || 'Failed to delete earnings');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEarnings = async () => {
    if (!createEarningsModal.referrerId || !createEarningsModal.referredId || !createEarningsModal.amount || !createEarningsModal.percentage) {
      showMessage('error', 'Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await adminApi.createManualReferralEarnings({
        referrerId: parseInt(createEarningsModal.referrerId),
        referredId: parseInt(createEarningsModal.referredId),
        level: parseInt(createEarningsModal.level),
        earningsType: createEarningsModal.earningsType,
        amount: parseFloat(createEarningsModal.amount),
        percentage: parseFloat(createEarningsModal.percentage),
      });
      if (response?.success) {
        showMessage('success', 'Earnings created successfully');
        setCreateEarningsModal({ 
          open: false, 
          referrerId: '', 
          referredId: '', 
          level: '1', 
          earningsType: 'buyin', 
          amount: '', 
          percentage: '' 
        });
        loadEarnings();
      }
    } catch (err) {
      console.error('Error creating earnings:', err);
      showMessage('error', err.message || 'Failed to create earnings');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount).toLocaleString(locale || 'it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString(locale || 'it-IT', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 1: return 'text-green-400 bg-green-400/10 border-green-400/20';
      case 2: return 'text-blue-400 bg-blue-400/10 border-blue-400/20';
      case 3: return 'text-purple-400 bg-purple-400/10 border-purple-400/20';
      default: return 'text-gray-400 bg-gray-400/10 border-gray-400/20';
    }
  };

  const getEarningsTypeLabel = (type) => {
    const labels = {
      buyin: 'Buy-in',
      marketplace_fee: 'Marketplace Fee',
      pack: 'Pack Purchase',
      auction: 'Auction'
    };
    return labels[type] || type;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold flex items-center">
          <Users className="w-8 h-8 mr-3 text-purple-400" />
          Affiliations Management
        </h1>
      </div>

      {message.text && (
        <div className={`mb-6 p-4 rounded-lg border ${
          message.type === 'success' 
            ? 'bg-green-900/50 border-green-700 text-green-300' 
            : 'bg-red-900/50 border-red-700 text-red-300'
        }`}>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-700 mb-6">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'network', label: 'Network Explorer', icon: Network },
          { id: 'referrals', label: 'Referrals', icon: Users },
          { id: 'earnings', label: 'Earnings', icon: DollarSign }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center px-4 py-2 border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-purple-500 text-purple-400'
                : 'border-transparent text-gray-400 hover:text-gray-300'
            }`}
          >
            <tab.icon className="w-5 h-5 mr-2" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400 mx-auto"></div>
              <p className="mt-4 text-gray-400">Loading statistics...</p>
            </div>
          ) : stats && (
            <>
              {/* Key Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-400">Total Referrers</span>
                    <Users className="w-5 h-5 text-purple-400" />
                  </div>
                  <div className="text-2xl font-bold text-white">{stats.totalReferrers}</div>
                </div>

                <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-400">Active Referrals</span>
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  </div>
                  <div className="text-2xl font-bold text-white">{stats.totalActiveReferrals}</div>
                </div>

                <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-400">Total Earnings</span>
                    <DollarSign className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div className="text-2xl font-bold text-green-400">{formatCurrency(stats.totalEarnings)} DIOS</div>
                </div>

                <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-400">Recent (30D)</span>
                    <TrendingUp className="w-5 h-5 text-blue-400" />
                  </div>
                  <div className="text-2xl font-bold text-blue-400">{formatCurrency(stats.recentEarnings)} DIOS</div>
                </div>
              </div>

              {/* Earnings by Level */}
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Award className="w-5 h-5 mr-2 text-purple-400" />
                  Earnings by Level
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[1, 2, 3].map(level => {
                    const levelData = stats.levelStats[level] || { total: 0, count: 0 };
                    return (
                      <div key={level} className={`p-4 rounded-lg border ${getLevelColor(level)}`}>
                        <div className="text-sm font-medium mb-1">Level {level} ({level === 1 ? '8%' : level === 2 ? '5%' : '2%'})</div>
                        <div className="text-xl font-bold">{formatCurrency(levelData.total)} DIOS</div>
                        <div className="text-xs mt-1 opacity-75">{levelData.count} transactions</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Earnings by Type */}
              {stats.typeStats && Object.keys(stats.typeStats).length > 0 && (
                <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <BarChart3 className="w-5 h-5 mr-2 text-blue-400" />
                    Earnings by Type
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {Object.entries(stats.typeStats).map(([type, data]) => (
                      <div key={type} className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                        <div className="text-sm text-gray-400 mb-1">{getEarningsTypeLabel(type)}</div>
                        <div className="text-lg font-bold text-white">{formatCurrency(data.total)} DIOS</div>
                        <div className="text-xs text-gray-500 mt-1">{data.count} transactions</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Referrers */}
              {topReferrers.length > 0 && (
                <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <Award className="w-5 h-5 mr-2 text-yellow-400" />
                    Top Referrers
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-700">
                          <th className="text-left py-3 px-4 text-gray-400">Rank</th>
                          <th className="text-left py-3 px-4 text-gray-400">Username</th>
                          <th className="text-left py-3 px-4 text-gray-400">Total Referrals</th>
                          <th className="text-right py-3 px-4 text-gray-400">Total Earnings</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topReferrers.map((referrer, idx) => (
                          <tr key={referrer.referrerId} className="border-b border-gray-700/50 hover:bg-gray-900/50">
                            <td className="py-3 px-4">
                              <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${
                                idx === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                                idx === 1 ? 'bg-gray-400/20 text-gray-400' :
                                idx === 2 ? 'bg-orange-500/20 text-orange-400' :
                                'bg-gray-700 text-gray-300'
                              }`}>
                                {idx + 1}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-white">{referrer.username}</td>
                            <td className="py-3 px-4 text-gray-300">{referrer.totalReferrals}</td>
                            <td className="py-3 px-4 text-right text-green-400 font-medium">
                              {formatCurrency(referrer.totalEarnings)} DIOS
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Network Explorer Tab */}
      {activeTab === 'network' && (
        <div className="space-y-6">
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search user by ID or username..."
                  value={networkSearch}
                  onChange={(e) => setNetworkSearch(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && networkSearch) {
                      const userId = parseInt(networkSearch);
                      if (!isNaN(userId)) {
                        loadUserNetwork(userId);
                      }
                    }
                  }}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
              </div>
              <button
                onClick={() => {
                  const userId = parseInt(networkSearch);
                  if (!isNaN(userId)) {
                    loadUserNetwork(userId);
                  } else {
                    showMessage('error', 'Please enter a valid user ID');
                  }
                }}
                className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center"
              >
                <Search className="w-4 h-4 mr-2" />
                Search
              </button>
            </div>
          </div>

          {networkLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400 mx-auto"></div>
              <p className="mt-4 text-gray-400">Loading network...</p>
            </div>
          ) : selectedUserNetwork ? (
            <div className="space-y-6">
              {/* User Info */}
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <User className="w-5 h-5 mr-2 text-purple-400" />
                  User Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400">Username</label>
                    <div className="text-white font-medium">{selectedUserNetwork.user.username}</div>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Email</label>
                    <div className="text-white">{selectedUserNetwork.user.email || 'N/A'}</div>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Referral Code</label>
                    <div className="text-white font-mono">{selectedUserNetwork.user.referralCode || 'N/A'}</div>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Total Earnings</label>
                    <div className="text-green-400 font-medium text-lg">
                      {formatCurrency(selectedUserNetwork.totalEarnings)} DIOS
                    </div>
                  </div>
                </div>
              </div>

              {/* Referrer */}
              {selectedUserNetwork.referrer && (
                <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                  <h3 className="text-lg font-semibold mb-4">Referrer (Who invited this user)</h3>
                  <div className="flex items-center justify-between p-4 bg-gray-900 rounded-lg">
                    <div>
                      <div className="text-white font-medium">{selectedUserNetwork.referrer.username}</div>
                      <div className="text-sm text-gray-400">{selectedUserNetwork.referrer.email}</div>
                    </div>
                    <button
                      onClick={() => {
                        setNetworkSearch(selectedUserNetwork.referrer.id.toString());
                        loadUserNetwork(selectedUserNetwork.referrer.id);
                      }}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View Network
                    </button>
                  </div>
                </div>
              )}

              {/* Network by Level */}
              {[1, 2, 3].map(level => {
                const levelData = selectedUserNetwork.network[`level${level}`] || [];
                if (levelData.length === 0) return null;
                
                return (
                  <div key={level} className={`bg-gray-800 rounded-lg p-6 border ${getLevelColor(level)}`}>
                    <h3 className="text-lg font-semibold mb-4">
                      Level {level} Referrals ({levelData.length})
                    </h3>
                    <div className="space-y-2">
                      {levelData.map((ref) => (
                        <div key={ref.id} className="flex items-center justify-between p-3 bg-gray-900 rounded-lg">
                          <div>
                            <div className="text-white font-medium">{ref.username}</div>
                            <div className="text-sm text-gray-400">
                              Earnings: {formatCurrency(ref.earningsFromUser)} DIOS
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setNetworkSearch(ref.referredId.toString());
                              loadUserNetwork(ref.referredId);
                            }}
                            className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
                          >
                            View
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-gray-800 rounded-lg p-12 border border-gray-700 text-center">
              <Network className="w-16 h-16 mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400">Enter a user ID or username to view their referral network</p>
            </div>
          )}
        </div>
      )}

      {/* Referrals Management Tab */}
      {activeTab === 'referrals' && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Filter className="w-5 h-5 mr-2" />
              Filters
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <input
                type="number"
                placeholder="Referrer ID"
                value={referralFilters.referrerId}
                onChange={(e) => setReferralFilters({ ...referralFilters, referrerId: e.target.value })}
                className="px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
              <input
                type="number"
                placeholder="Referred ID"
                value={referralFilters.referredId}
                onChange={(e) => setReferralFilters({ ...referralFilters, referredId: e.target.value })}
                className="px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
              <select
                value={referralFilters.level}
                onChange={(e) => setReferralFilters({ ...referralFilters, level: e.target.value })}
                className="px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              >
                <option value="">All Levels</option>
                <option value="1">Level 1</option>
                <option value="2">Level 2</option>
                <option value="3">Level 3</option>
              </select>
              <select
                value={referralFilters.status}
                onChange={(e) => setReferralFilters({ ...referralFilters, status: e.target.value })}
                className="px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="blocked">Blocked</option>
              </select>
              <input
                type="date"
                placeholder="From Date"
                value={referralFilters.dateFrom}
                onChange={(e) => setReferralFilters({ ...referralFilters, dateFrom: e.target.value })}
                className="px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
              <input
                type="date"
                placeholder="To Date"
                value={referralFilters.dateTo}
                onChange={(e) => setReferralFilters({ ...referralFilters, dateTo: e.target.value })}
                className="px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>

          {/* Referrals Table */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400 mx-auto"></div>
              <p className="mt-4 text-gray-400">Loading referrals...</p>
            </div>
          ) : (
            <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-3 px-4 text-gray-400">Referrer</th>
                      <th className="text-left py-3 px-4 text-gray-400">Referred</th>
                      <th className="text-left py-3 px-4 text-gray-400">Level</th>
                      <th className="text-left py-3 px-4 text-gray-400">Status</th>
                      <th className="text-left py-3 px-4 text-gray-400">Earnings</th>
                      <th className="text-left py-3 px-4 text-gray-400">Created</th>
                      <th className="text-right py-3 px-4 text-gray-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referrals.map((referral) => (
                      <tr key={referral.id} className="border-b border-gray-700/50 hover:bg-gray-900/50">
                        <td className="py-3 px-4">
                          <div className="text-white">{referral.referrerUsername}</div>
                          <div className="text-xs text-gray-500">ID: {referral.referrerId}</div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-white">{referral.referredUsername}</div>
                          <div className="text-xs text-gray-500">ID: {referral.referredId}</div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getLevelColor(referral.level)}`}>
                            L{referral.level}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {referral.status === 'active' ? (
                            <span className="px-2 py-1 rounded text-xs font-medium bg-green-400/10 text-green-400 border border-green-400/20">
                              Active
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded text-xs font-medium bg-red-400/10 text-red-400 border border-red-400/20">
                              Blocked
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-green-400 font-medium">
                          {formatCurrency(referral.totalEarnings)} DIOS
                        </td>
                        <td className="py-3 px-4 text-gray-400 text-sm">
                          {formatDate(referral.createdAt)}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setChangeReferrerModal({ open: true, referralId: referral.id, newReferrerId: '' })}
                              className="p-2 text-gray-400 hover:text-purple-400 transition-colors"
                              title="Change Referrer"
                            >
                              <UserPlus className="w-4 h-4" />
                            </button>
                            {referral.status === 'active' ? (
                              <button
                                onClick={() => handleUpdateReferralStatus(referral.id, 'blocked')}
                                className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                                title="Block"
                              >
                                <Ban className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleUpdateReferralStatus(referral.id, 'active')}
                                className="p-2 text-gray-400 hover:text-green-400 transition-colors"
                                title="Unblock"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {referrals.length === 0 && (
                  <div className="text-center py-12 text-gray-400">
                    No referrals found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Earnings Tab */}
      {activeTab === 'earnings' && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Filter className="w-5 h-5 mr-2" />
              Filters
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <input
                type="number"
                placeholder="Referrer ID"
                value={earningsFilters.referrerId}
                onChange={(e) => setEarningsFilters({ ...earningsFilters, referrerId: e.target.value })}
                className="px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
              <input
                type="number"
                placeholder="Referred ID"
                value={earningsFilters.referredId}
                onChange={(e) => setEarningsFilters({ ...earningsFilters, referredId: e.target.value })}
                className="px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
              <select
                value={earningsFilters.level}
                onChange={(e) => setEarningsFilters({ ...earningsFilters, level: e.target.value })}
                className="px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              >
                <option value="">All Levels</option>
                <option value="1">Level 1</option>
                <option value="2">Level 2</option>
                <option value="3">Level 3</option>
              </select>
              <select
                value={earningsFilters.earningsType}
                onChange={(e) => setEarningsFilters({ ...earningsFilters, earningsType: e.target.value })}
                className="px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              >
                <option value="">All Types</option>
                <option value="buyin">Buy-in</option>
                <option value="marketplace_fee">Marketplace Fee</option>
                <option value="pack">Pack</option>
                <option value="auction">Auction</option>
              </select>
              <input
                type="date"
                placeholder="From Date"
                value={earningsFilters.dateFrom}
                onChange={(e) => setEarningsFilters({ ...earningsFilters, dateFrom: e.target.value })}
                className="px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
              <input
                type="date"
                placeholder="To Date"
                value={earningsFilters.dateTo}
                onChange={(e) => setEarningsFilters({ ...earningsFilters, dateTo: e.target.value })}
                className="px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>

          {/* Earnings Table */}
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-white">Earnings Records</h3>
            <button
              onClick={() => setCreateEarningsModal({ 
                open: true, 
                referrerId: '', 
                referredId: '', 
                level: '1', 
                earningsType: 'buyin', 
                amount: '', 
                percentage: '', 
                originalAmount: '' 
              })}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create Manual Earnings
            </button>
          </div>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400 mx-auto"></div>
              <p className="mt-4 text-gray-400">Loading earnings...</p>
            </div>
          ) : (
            <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-3 px-4 text-gray-400">Date</th>
                      <th className="text-left py-3 px-4 text-gray-400">Referrer</th>
                      <th className="text-left py-3 px-4 text-gray-400">Referred</th>
                      <th className="text-left py-3 px-4 text-gray-400">Type</th>
                      <th className="text-left py-3 px-4 text-gray-400">Level</th>
                      <th className="text-right py-3 px-4 text-gray-400">Percentage</th>
                      <th className="text-right py-3 px-4 text-gray-400">Earnings</th>
                      <th className="text-right py-3 px-4 text-gray-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earnings.map((earning) => (
                      <tr key={earning.id} className="border-b border-gray-700/50 hover:bg-gray-900/50">
                        <td className="py-3 px-4 text-gray-400 text-sm">
                          {formatDate(earning.createdAt)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-white">{earning.referrerUsername}</div>
                          <div className="text-xs text-gray-500">ID: {earning.referrerId}</div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-white">{earning.referredUsername}</div>
                          <div className="text-xs text-gray-500">ID: {earning.referredId}</div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-1 rounded text-xs font-medium bg-blue-400/10 text-blue-400 border border-blue-400/20">
                            {getEarningsTypeLabel(earning.earningsType)}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getLevelColor(earning.level)}`}>
                            L{earning.level}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right text-gray-300">
                          {earning.percentage}%
                        </td>
                        <td className="py-3 px-4 text-right text-green-400 font-medium">
                          {formatCurrency(earning.amount)} DIOS
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setEditEarningsModal({ open: true, earning, newAmount: earning.amount.toString() })}
                              className="p-2 text-gray-400 hover:text-blue-400 transition-colors"
                              title="Edit Amount"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteEarnings(earning.id)}
                              className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {earnings.length === 0 && (
                  <div className="text-center py-12 text-gray-400">
                    No earnings found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit Earnings Modal */}
      {editEarningsModal.open && editEarningsModal.earning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center">
                <Edit className="w-5 h-5 mr-2 text-purple-400" />
                Edit Earnings Amount
              </h3>
              <button
                onClick={() => setEditEarningsModal({ open: false, earning: null, newAmount: '' })}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Current Amount</label>
                <div className="px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-400">
                  {formatCurrency(editEarningsModal.earning.amount)} DIOS
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">New Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={editEarningsModal.newAmount}
                  onChange={(e) => setEditEarningsModal({ ...editEarningsModal, newAmount: e.target.value })}
                  placeholder="Enter new amount"
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setEditEarningsModal({ open: false, earning: null, newAmount: '' })}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditEarnings}
                  disabled={loading}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {loading ? <Loader className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Update
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Earnings Modal */}
      {createEarningsModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center">
                <Plus className="w-5 h-5 mr-2 text-purple-400" />
                Create Manual Earnings
              </h3>
              <button
                onClick={() => setCreateEarningsModal({ 
                  open: false, 
                  referrerId: '', 
                  referredId: '', 
                  level: '1', 
                  earningsType: 'buyin', 
                  amount: '', 
                  percentage: '', 
                  originalAmount: '' 
                })}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Referrer ID *</label>
                  <input
                    type="number"
                    value={createEarningsModal.referrerId}
                    onChange={(e) => setCreateEarningsModal({ ...createEarningsModal, referrerId: e.target.value })}
                    placeholder="Referrer user ID"
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Referred ID *</label>
                  <input
                    type="number"
                    value={createEarningsModal.referredId}
                    onChange={(e) => setCreateEarningsModal({ ...createEarningsModal, referredId: e.target.value })}
                    placeholder="Referred user ID"
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Level *</label>
                  <select
                    value={createEarningsModal.level}
                    onChange={(e) => setCreateEarningsModal({ ...createEarningsModal, level: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  >
                    <option value="1">Level 1</option>
                    <option value="2">Level 2</option>
                    <option value="3">Level 3</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Earnings Type *</label>
                  <select
                    value={createEarningsModal.earningsType}
                    onChange={(e) => setCreateEarningsModal({ ...createEarningsModal, earningsType: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  >
                    <option value="buyin">Buy-in</option>
                    <option value="marketplace_fee">Marketplace Fee</option>
                    <option value="pack">Pack</option>
                    <option value="auction">Auction</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Amount (D10S) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={createEarningsModal.amount}
                    onChange={(e) => setCreateEarningsModal({ ...createEarningsModal, amount: e.target.value })}
                    placeholder="Earnings amount"
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Percentage (%) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={createEarningsModal.percentage}
                    onChange={(e) => setCreateEarningsModal({ ...createEarningsModal, percentage: e.target.value })}
                    placeholder="8, 5, or 2"
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setCreateEarningsModal({ 
                    open: false, 
                    referrerId: '', 
                    referredId: '', 
                    level: '1', 
                    earningsType: 'buyin', 
                    amount: '', 
                    percentage: '', 
                    originalAmount: '' 
                  })}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateEarnings}
                  disabled={loading}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {loading ? <Loader className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Referrer Modal */}
      {changeReferrerModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center">
                <UserPlus className="w-5 h-5 mr-2 text-purple-400" />
                Change Referrer
              </h3>
              <button
                onClick={() => setChangeReferrerModal({ open: false, referralId: null, newReferrerId: '' })}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">New Referrer ID</label>
                <input
                  type="number"
                  value={changeReferrerModal.newReferrerId}
                  onChange={(e) => setChangeReferrerModal({ ...changeReferrerModal, newReferrerId: e.target.value })}
                  placeholder="Enter new referrer user ID"
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setChangeReferrerModal({ open: false, referralId: null, newReferrerId: '' })}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleChangeReferrer}
                  disabled={loading}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {loading ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Changing...
                    </>
                  ) : (
                    'Change Referrer'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAffiliations;
